import { FC, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import { ServiceSupplier } from '../../services/service-supplier';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  supplierIds: string[];
  onSave: () => void;
};

const BulkDeleteSuppliersModal: FC<Props> = ({ show, setShow, supplierIds, onSave }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'bulkActions.deleteModal' });
  const [confirmText, setConfirmText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const confirmWord = t('confirmWord', 'ta bort');
  const isConfirmed = confirmText.trim().toLowerCase() === confirmWord.toLowerCase();

  const handleSave = async () => {
    if (!isConfirmed) return;
    setIsSaving(true);
    try {
      const result = await ServiceSupplier.bulkDelete(supplierIds);
      NotificationManager.success(
        t('notification.success', '{{count}} leverantörer borttagna', { count: result.deleted }),
        t('notification.title', 'Borttaget')
      );
      setConfirmText('');
      setShow(false);
      onSave();
    } catch (e) {
      const errorMsg = (e as any)?.response?.data?.error ?? t('notification.error', 'Fel');
      NotificationManager.error(errorMsg, t('notification.error', 'Fel'));
    }
    setIsSaving(false);
  };

  return (
    <Modal
      show={show}
      setShow={(v) => { setConfirmText(''); setShow(v); }}
      title={t('title', {
        count: supplierIds.length,
        defaultValue_one: 'Ta bort {{count}} leverantör',
        defaultValue_other: 'Ta bort {{count}} leverantörer',
      })}
      buttonInfo={{
        label: t('button', 'Ta bort'),
        action: handleSave,
        loaderShow: isSaving,
        disabled: !isConfirmed,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px' }}>
        <p style={{ color: 'var(--gray-700)' }}>
          {t('description', 'Leverantören tas bort från systemet. Detta kan påverka köp, anbud, konversationer, anteckningar och annan information där leverantören ingår.')}
        </p>
        <p style={{ color: 'var(--gray-700)' }}>
          {t('confirmPrompt', 'Skriv "{{word}}" för att bekräfta.', { word: confirmWord })}
        </p>
        <Field
          label={t('confirmLabel', 'Bekräfta')}
          value={confirmText}
          onChange={setConfirmText}
          placeholder={confirmWord}
        />
      </div>
    </Modal>
  );
};

export default BulkDeleteSuppliersModal;
